import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { DatePicker } from '../components/date-picker';
import { FormFooter, PageHeader, Row, Section, Select, Tabs } from '../components/form';
import { Alert, Badge, Button, Input } from '../components/ui';
import { api } from '../lib/api';

interface Window {
  dowStart: number | null;
  dowEnd: number | null;
  todStart: string | null;
  todEnd: string | null;
  monthStart: number | null;
  monthEnd: number | null;
  domStart: number | null;
  domEnd: number | null;
}

interface TimeCondition {
  id: string;
  name: string;
  timezone: string;
  windows?: Window[];
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The zones a small business actually picks from, plus whatever the browser reports. */
const ZONES = Array.from(
  new Set([
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'UTC',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Bangkok',
    'Asia/Singapore',
    'Asia/Yangon',
    'Australia/Sydney',
  ]),
);

const OFFICE_HOURS: Window = {
  dowStart: 1,
  dowEnd: 5,
  todStart: '09:00',
  todEnd: '17:00',
  monthStart: null,
  monthEnd: null,
  domStart: null,
  domEnd: null,
};

function describe(window: Window): string {
  const days =
    window.dowStart === null || window.dowEnd === null
      ? 'Every day'
      : window.dowStart === window.dowEnd
        ? DAYS[window.dowStart]!
        : `${DAYS[window.dowStart]!} to ${DAYS[window.dowEnd]!}`;

  const time = window.todStart && window.todEnd ? `${window.todStart}–${window.todEnd}` : 'all day';

  return `${days}, ${time}`;
}

/**
 * Business hours and holidays.
 *
 * A condition sends calls one way inside its schedule and another way outside it, which is how
 * "ring the team during the day, go to voicemail at night" is expressed without two inbound routes.
 */
export function TimeConditions() {
  const [tab, setTab] = useState('hours');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business hours and holidays"
        description="Route calls differently inside and outside a schedule. Holidays close the office regardless."
      />

      <Tabs
        tabs={[
          { id: 'hours', label: 'Business hours' },
          { id: 'holidays', label: 'Holidays' },
          { id: 'emergency', label: 'Emergency numbers' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'hours' && <ConditionList />}
      {tab === 'holidays' && <HolidayList />}
      {tab === 'emergency' && <EmergencyList />}
    </div>
  );
}

function ConditionList() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: conditions = [] } = useQuery({
    queryKey: ['time-conditions'],
    queryFn: () => api.get<TimeCondition[]>('/api/v1/time-conditions'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/time-conditions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time-conditions'] }),
  });

  const columns: Array<Column<TimeCondition>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (c) => <span className="font-medium">{c.name}</span>,
    },
    {
      key: 'timezone',
      header: 'Timezone',
      sort: (a, b) => a.timezone.localeCompare(b.timezone),
      render: (c) => <Badge>{c.timezone}</Badge>,
    },
  ];

  if (creating || editing) {
    return (
      <Editor
        id={editing}
        onDone={() => {
          setCreating(false);
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['time-conditions'] });
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataTable
        toolbar={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            New condition
          </Button>
        }
        rows={conditions}
        columns={columns}
        rowKey={(c) => c.id}
        search={(c) => `${c.name} ${c.timezone}`}
        searchPlaceholder="Search conditions"
        onEdit={(c) => setEditing(c.id)}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        deleteLabel={(rows) =>
          `Delete ${rows.length} condition(s)? Anything routing through them will need a new destination.`
        }
        empty="No time conditions yet."
      />
    </div>
  );
}

function Editor({ id, onDone }: { id: string | null; onDone: () => void }) {
  const existing = useQuery({
    queryKey: ['time-condition', id],
    queryFn: () => api.get<TimeCondition>(`/api/v1/time-conditions/${id}`),
    enabled: id !== null,
  });

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(ZONES[0] ?? 'UTC');
  const [windows, setWindows] = useState<Window[]>([OFFICE_HOURS]);
  const [match, setMatch] = useState<DestinationValue>({
    destinationType: 'hangup',
    destinationId: null,
    destinationValue: null,
  });
  const [nomatch, setNomatch] = useState<DestinationValue>({
    destinationType: 'hangup',
    destinationId: null,
    destinationValue: null,
  });
  const [loaded, setLoaded] = useState(false);

  // Populated once the existing record arrives; re-running would discard edits in progress.
  if (id !== null && existing.data && !loaded) {
    const row = existing.data as TimeCondition & Record<string, unknown>;
    setName(row.name);
    setTimezone(row.timezone);
    setWindows(row.windows?.length ? row.windows : [OFFICE_HOURS]);
    setMatch({
      destinationType: String(row['matchDestinationType'] ?? 'hangup'),
      destinationId: (row['matchDestinationId'] as string | null) ?? null,
      destinationValue: (row['matchDestinationValue'] as string | null) ?? null,
    });
    setNomatch({
      destinationType: String(row['nomatchDestinationType'] ?? 'hangup'),
      destinationId: (row['nomatchDestinationId'] as string | null) ?? null,
      destinationValue: (row['nomatchDestinationValue'] as string | null) ?? null,
    });
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { name, timezone, windows, match, nomatch };
      return id === null
        ? api.post('/api/v1/time-conditions', body)
        : api.patch(`/api/v1/time-conditions/${id}`, body);
    },
    onSuccess: onDone,
  });

  const patch = (index: number, next: Partial<Window>) =>
    setWindows((current) => current.map((w, i) => (i === index ? { ...w, ...next } : w)));

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader title={id === null ? 'New time condition' : `Edit ${name}`} />

      {save.error && <Alert>{(save.error as Error).message}</Alert>}

      <Section title="Basic">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Office hours"
              required
            />
          </Row>

          <Row label="Timezone" required hint="Evaluated here, not in the server's zone.">
            <Select value={timezone} onChange={setTimezone}>
              {ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </Select>
          </Row>
        </div>
      </Section>

      <Section
        title="Open hours"
        description="Calls arriving inside any of these windows take the open destination."
      >
        <div className="space-y-3">
          {windows.map((window, index) => (
            <div key={index} className="rounded-[--radius] border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{describe(window)}</span>
                <button
                  type="button"
                  onClick={() => setWindows((c) => c.filter((_, i) => i !== index))}
                  className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Remove window"
                >
                  <Trash2 size={15} />
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                <Row label="From day">
                  <Select
                    value={String(window.dowStart ?? '')}
                    onChange={(v) => patch(index, { dowStart: v === '' ? null : Number(v) })}
                  >
                    <option value="">Any</option>
                    {DAYS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </Select>
                </Row>

                <Row label="To day">
                  <Select
                    value={String(window.dowEnd ?? '')}
                    onChange={(v) => patch(index, { dowEnd: v === '' ? null : Number(v) })}
                  >
                    <option value="">Any</option>
                    {DAYS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </Select>
                </Row>

                <Row label="From time">
                  <Input
                    type="time"
                    value={window.todStart ?? ''}
                    onChange={(e) => patch(index, { todStart: e.target.value || null })}
                  />
                </Row>

                <Row label="To time" hint="Exclusive, so 17:00 is already closed.">
                  <Input
                    type="time"
                    value={window.todEnd ?? ''}
                    onChange={(e) => patch(index, { todEnd: e.target.value || null })}
                  />
                </Row>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={() => setWindows((c) => [...c, OFFICE_HOURS])}
          >
            <Plus size={14} />
            Add window
          </Button>

          {windows.length === 0 && (
            <Alert tone="warning">
              With no windows the condition is always closed, and every call takes the closed
              destination.
            </Alert>
          )}
        </div>
      </Section>

      <Section title="Destinations">
        <div className="grid gap-4 sm:grid-cols-2">
          <DestinationPicker label="When open" value={match} onChange={setMatch} />
          <DestinationPicker label="When closed" value={nomatch} onChange={setNomatch} />
        </div>
      </Section>

      <FormFooter onCancel={onDone} saving={save.isPending} disabled={!name} />
    </form>
  );
}

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  repeatsAnnually: boolean;
}

function HolidayList() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    repeatsAnnually: false,
  });

  const { data: rows = [] } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => api.get<Holiday[]>('/api/v1/holidays'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['holidays'] });

  const create = useMutation({
    mutationFn: () => api.post('/api/v1/holidays', form),
    onSuccess: () => {
      setCreating(false);
      setForm({ name: '', startDate: '', endDate: '', repeatsAnnually: false });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/holidays/${id}`),
    onSuccess: invalidate,
  });

  const columns: Array<Column<Holiday>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (h) => <span className="font-medium">{h.name}</span>,
    },
    {
      key: 'dates',
      header: 'Dates',
      sort: (a, b) => a.startDate.localeCompare(b.startDate),
      render: (h) => (
        <span className="tabular-nums">
          {h.startDate === h.endDate ? h.startDate : `${h.startDate} to ${h.endDate}`}
        </span>
      ),
    },
    {
      key: 'repeat',
      header: 'Repeats',
      render: (h) =>
        h.repeatsAnnually ? <Badge>Every year</Badge> : <Badge tone="muted">Once</Badge>,
    },
  ];

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        A holiday closes the office for every schedule, whatever the weekly hours say. Calls take
        the closed destination of whichever time condition they pass through.
      </Alert>

      {creating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title="New holiday">
            <div className="grid gap-4 sm:grid-cols-4">
              <Row label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Christmas Day"
                />
              </Row>
              <Row label="From" required>
                <DatePicker
                  value={form.startDate}
                  onChange={(startDate) => setForm((f) => ({ ...f, startDate }))}
                  clearable={false}
                  aria-label="Holiday start date"
                />
              </Row>
              <Row label="To" required hint="Same day for a single-day holiday.">
                <DatePicker
                  value={form.endDate}
                  onChange={(endDate) => setForm((f) => ({ ...f, endDate }))}
                  clearable={false}
                  aria-label="Holiday end date"
                />
              </Row>
              <Row label="Repeats" hint="Annual holidays ignore the year.">
                <Select
                  value={form.repeatsAnnually ? 'yes' : 'no'}
                  onChange={(v) => setForm((f) => ({ ...f, repeatsAnnually: v === 'yes' }))}
                >
                  <option value="no">Once</option>
                  <option value="yes">Every year</option>
                </Select>
              </Row>
            </div>

            {create.error && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !form.name || !form.startDate}>
                Create
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(h) => h.id}
        search={(h) => h.name}
        searchPlaceholder="Search holidays"
        toolbar={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            New holiday
          </Button>
        }
        onDelete={(selection) => selection.forEach((h) => remove.mutate(h.id))}
        empty="No holidays. Business hours apply every day of the year."
      />
    </div>
  );
}

interface EmergencyNumber {
  id: string;
  number: string;
  trunkId: string | null;
  outboundCid: string | null;
  notifyEmails: string[];
}

function EmergencyList() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ number: '', trunkId: '', outboundCid: '', notify: '' });

  const { data: rows = [] } = useQuery({
    queryKey: ['emergency-numbers'],
    queryFn: () => api.get<EmergencyNumber[]>('/api/v1/emergency-numbers'),
  });

  const { data: trunks = [] } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/api/v1/trunks'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['emergency-numbers'] });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/v1/emergency-numbers', {
        number: form.number,
        trunkId: form.trunkId || null,
        ...(form.outboundCid ? { outboundCid: form.outboundCid } : {}),
        notifyEmails: form.notify
          .split(',')
          .map((e) => e.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setCreating(false);
      setForm({ number: '', trunkId: '', outboundCid: '', notify: '' });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/emergency-numbers/${id}`),
    onSuccess: invalidate,
  });

  const columns: Array<Column<EmergencyNumber>> = [
    {
      key: 'number',
      header: 'Number',
      sort: (a, b) => a.number.localeCompare(b.number),
      render: (e) => <code className="font-medium">{e.number}</code>,
    },
    {
      key: 'trunk',
      header: 'Trunk',
      render: (e) =>
        e.trunkId ? (
          (trunks.find((t) => t.id === e.trunkId)?.name ?? 'Unknown')
        ) : (
          <span className="text-muted-foreground">First available</span>
        ),
    },
    {
      key: 'cid',
      header: 'Caller ID',
      render: (e) =>
        e.outboundCid ?? <span className="text-muted-foreground">Extension's own</span>,
    },
    {
      key: 'notify',
      header: 'Notifies',
      render: (e) =>
        e.notifyEmails.length > 0 ? (
          e.notifyEmails.join(', ')
        ) : (
          <span className="text-muted-foreground">Nobody</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        Emergency numbers bypass blocked numbers, PIN codes, outbound restrictions and dial
        patterns. That is deliberate: every one of those is a way to accidentally prevent somebody
        reaching emergency services.
      </Alert>

      {rows.length === 0 && (
        <Alert>
          No emergency numbers are configured. Calls to 999, 112 or 911 will be treated as ordinary
          outbound calls and may be blocked by your dial patterns.
        </Alert>
      )}

      {creating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title="New emergency number">
            <div className="grid gap-4 sm:grid-cols-4">
              <Row label="Number" required>
                <Input
                  value={form.number}
                  onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                  placeholder="999"
                />
              </Row>
              <Row label="Trunk" hint="Leave blank to use normal routing.">
                <Select
                  value={form.trunkId}
                  onChange={(v) => setForm((f) => ({ ...f, trunkId: v }))}
                >
                  <option value="">First available</option>
                  {trunks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </Row>
              <Row
                label="Caller ID"
                hint="Emergency services route on this, so it should identify the site."
              >
                <Input
                  value={form.outboundCid}
                  onChange={(e) => setForm((f) => ({ ...f, outboundCid: e.target.value }))}
                />
              </Row>
              <Row label="Notify" hint="Comma separated. Told whenever this number is dialled.">
                <Input
                  value={form.notify}
                  onChange={(e) => setForm((f) => ({ ...f, notify: e.target.value }))}
                  placeholder="reception@example.com"
                />
              </Row>
            </div>

            {create.error && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !form.number}>
                Create
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(e) => e.id}
        search={(e) => e.number}
        searchPlaceholder="Search numbers"
        toolbar={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            New emergency number
          </Button>
        }
        onDelete={(selection) => selection.forEach((e) => remove.mutate(e.id))}
        deleteLabel={() =>
          'Remove this emergency number? Calls to it will follow ordinary routing.'
        }
        empty="No emergency numbers configured."
      />
    </div>
  );
}
